import React from "react";
import { cn } from "../../lib/utils";

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "primary" | "success" | "destructive" | "info" | "warning";
  close?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

export function Alert({
  className,
  variant = "primary",
  close = false,
  onClose,
  children,
  ...props
}: AlertProps) {
  const variantStyles: Record<string, string> = {
    primary: "bg-[#1A1A1A] border border-[#2A2A2A] text-white",
    success: "bg-[#1A1A1A] border border-[#FF541F] text-white", // Using brand orange instead of green
    destructive: "bg-[#1A1A1A] border border-red-800 text-white",
    info: "bg-[#1A1A1A] border border-violet-800 text-white",
    warning: "bg-[#1A1A1A] border border-amber-700 text-white",
  };

  return (
    <div
      role="alert"
      className={cn(
        "flex items-center w-full rounded-xl px-6 py-3 gap-3 backdrop-blur-sm shadow-lg",
        "z-[10000] fixed top-10 left-1/2 -translate-x-1/2 max-w-[90vw] md:max-w-xl", // <-- Add high z-index and fixed positioning
        "bg-[#1A1A1A]/95", // <-- Slightly more opaque background for clarity
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {children}
      {close && (
        <button
          onClick={onClose}
          className="ml-auto text-white/70 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

export function AlertIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
      {children}
    </span>
  );
}

export function AlertTitle({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-medium tracking-wide text-sm">
      {children}
    </span>
  );
}

export function AlertToolbar({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-auto flex items-center gap-2">
      {children}
    </span>
  );
}